<template>
  <ModuleContainer v-bind="bindModule">
    <template #btn>
      <div class="button-container d-flex">
        <div
          class="maplibregl-ctrl-mouse-coordinates-loading mr-2"
          v-if="data_loading"
        >
          <v-icon>mdi-spin mdi-loading</v-icon>
        </div>
        <div
          class="flex-grow-1 d-flex maplibregl-ctrl-mouse-coordinates px-2 mr-2 align-center"
        >
          <div class="flex-grow-0 d-flex pr-2">
            <i :class="[zoomIcon]" title="Current Zoom" />
            <div style="margin-left: 4px">{{ currentZoom }}</div>
          </div>
          <div class="flex-grow-0 d-flex pr-2">
            <i
              class="icon-clickable"
              :class="icon"
              title="Add Marker"
              @click="onAddMarker"
            />

            <div style="margin-left: 4px" class="selectable">
              {{ currentPoint.longitude }}, {{ currentPoint.latitude }}
            </div>
          </div>
        </div>

        <div
          ref="scale"
          class="flex-grow-0 d-flex align-center justify-center maplibregl-ctrl-scale-custom"
        ></div>
      </div>
    </template>
  </ModuleContainer>
</template>

<script>
import ModuleMixin from '@components/Map/mixins/ModuleMixin'
import { debounce } from '@utils'
import { deg_to_dms_string, latDMS, lngDMS } from '@utils/CRS'
import mapboxgl from 'mapbox-gl'
import proj4 from 'proj4'

const defaultOptions = {
  unit: 'metric',
}
export default {
  mixins: [ModuleMixin],

  props: {
    icon: {
      type: String,
      default: 'mdi mdi-map-marker-outline',
    },

    zoomIcon: {
      type: String,
      default: 'mdi mdi-magnify',
    },
  },

  data() {
    return {
      data_loading: false,
      items: [],
      lngLat: { latitude: 0, longitude: 0 },
      currentZoom: 0,
      currentPoint: { latitude: '', longitude: 0 },
      isDMS: false,
    }
  },
  computed: {
    crs: {
      get() {
        return 'EPSG:4326'
      },
    },
  },

  methods: {
    changeDisplayTypePixelValue() {
      this.isDMS = !this.isDMS
      this.changePixelValue()
    },
    changePixelValue() {
      if (this.isDMS) {
        this.currentPoint.longitude = latDMS(+this.lngLat.longitude)
        this.currentPoint.latitude = latDMS(+this.lngLat.latitude)
      } else {
        this.currentPoint.longitude = this.lngLat.longitude
        this.currentPoint.latitude = this.lngLat.latitude
      }
    },

    onInit() {
      this.bindedOnMouseMove = this.onMouseMove.bind(this)
      this.bindedOnZoomEnd = this.onZoomEnd.bind(this)
      this.bindedOnMapMove = this.onMapMove.bind(this)

      this.map.on('move', this.bindedOnMapMove)
      this.map.on('mousemove', this.bindedOnMouseMove)
      this.map.on('zoomend', this.bindedOnZoomEnd)

      this.options = Object.assign({}, defaultOptions)
      this.currentZoom = this.map.getZoom().toFixed(2)

      let center = this.map.getCenter()
      let point = this.format([center.lng, center.lat])
      this.lngLat.latitude = point[1]
      this.lngLat.longitude = point[0]
      this.changePixelValue()

      this.$nextTick(() => {
        this.onMapMove()
      })
      this.bindedDataLoading = (e) => {
        this.data_loading = !e.isSourceLoaded
      }
      this.map.on('data', this.bindedDataLoading)
    },
    onDestroy() {
      this.map.off('zoomend', this.bindedOnZoomEnd)
      this.map.off('mousemove', this.bindedOnMouseMove)
      this.map.off('move', this.bindedOnMapMove)
      this.map.off('data', this.bindedDataLoading)
    },
    format(point) {
      if (this.crs !== 'EPSG:4326') {
        let temp_point = proj4(this.crs, [
          parseFloat(point[0]),
          parseFloat(point[1]),
        ])
        point[0] = temp_point[0].toFixed(0)
        point[1] = temp_point[1].toFixed(0)
      } else {
        point[0] = Number.parseFloat(point[0]).toFixed(4)
        point[1] = Number.parseFloat(point[1]).toFixed(4)
      }
      return point
    },
    onMapMove() {
      updateScale(this.map, this.$refs.scale, this.options)
    },
    onMouseMove: debounce(function(e) {
      let point = this.format([e.lngLat.lng, e.lngLat.lat])
      this.lngLat.latitude = point[1]
      this.lngLat.longitude = point[0]
      this.changePixelValue()
    }, 15),

    onZoomEnd() {
      this.currentZoom = this.map.getZoom().toFixed(2)
    },

    addMarker(lngLat) {
      const text = window.document.createElement('div')
      text.className = 'selectable'
      text.innerText = `${deg_to_dms_string(
        lngLat.lng.toFixed(8)
      )}, ${deg_to_dms_string(lngLat.lat.toFixed(8))}`

      const removeMarkerButton = window.document.createElement('i')
      removeMarkerButton.className = 'mdi mdi-delete'
      removeMarkerButton.title = 'Remove Marker'
      removeMarkerButton.style.marginLeft = '4px'
      removeMarkerButton.style.cursor = 'pointer'
      removeMarkerButton.style.fontSize = '14px'
      removeMarkerButton.style.width = '14px'
      removeMarkerButton.style.height = '14px'
      removeMarkerButton.style.lineHeight = '14px'
      removeMarkerButton.addEventListener('click', () => {
        this.removeMarker()
      })

      const container = window.document.createElement('div')
      container.style.display = 'flex'
      container.style.alignItems = 'center'

      container.append(text, removeMarkerButton)

      const popup = new mapboxgl.Popup({
        closeButton: false,
      }).setDOMContent(container)

      this.marker = new mapboxgl.Marker()
        .setLngLat(lngLat)
        .setPopup(popup)
        .addTo(this.map)

      this.marker.getElement().style.cursor = 'pointer'

      this.marker.togglePopup()
    },

    removeMarker() {
      if (!this.marker) return

      this.marker.remove()
    },

    onMapClick(e) {
      this.addMarker(e.lngLat)
      this.changeCursor('')
    },

    changeCursor(cursor) {
      this.map.getCanvas().style.cursor = cursor
    },
    onAddMarker() {
      this.removeMarker()

      this.changeCursor('crosshair')

      this.map.once('click', this.onMapClick.bind(this))
    },
  },
}

function updateScale(map, container, options) {
  // A horizontal scale is imagined to be present at center of the map
  // container with maximum length (Default) as 100px.
  // Using spherical law of cosines approximation, the real distance is
  // found between the two coordinates.
  const maxWidth = (options && options.maxWidth) || 100

  const y = map._container.clientHeight / 2
  const left = map.unproject([0, y])
  const right = map.unproject([maxWidth, y])
  const maxMeters = left.distanceTo(right)
  // The real distance corresponding to 100px scale length is rounded off to
  // near pretty number and the scale length for the same is found out.
  // Default unit of the scale is based on User's locale.
  if (options && options.unit === 'imperial') {
    const maxFeet = 3.2808 * maxMeters
    if (maxFeet > 5280) {
      const maxMiles = maxFeet / 5280
      setScale(container, maxMiles, map._getUIString('ScaleControl.Miles'))
    } else {
      setScale(container, maxFeet, map._getUIString('ScaleControl.Feet'))
    }
  } else if (options && options.unit === 'nautical') {
    const maxNauticals = maxMeters / 1852
    setScale(
      container,
      maxNauticals,
      map._getUIString('ScaleControl.NauticalMiles')
    )
  } else if (maxMeters >= 1000) {
    setScale(
      container,
      maxMeters / 1000,
      map._getUIString('ScaleControl.Kilometers')
    )
  } else {
    setScale(container, maxMeters, map._getUIString('ScaleControl.Meters'))
  }
}

function setScale(container, maxDistance, unit) {
  const distance = getRoundNum(maxDistance)
  if (container) container.innerHTML = `${distance}&nbsp;${unit}`
}

function getDecimalRoundNum(d) {
  const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10))
  return Math.round(d * multiplier) / multiplier
}

function getRoundNum(num) {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1)
  let d = num / pow10

  d =
    d >= 10
      ? 10
      : d >= 5
      ? 5
      : d >= 3
      ? 3
      : d >= 2
      ? 2
      : d >= 1
      ? 1
      : getDecimalRoundNum(d)

  return pow10 * d
}
</script>

<style scoped>
.maplibregl-ctrl-mouse-coordinates-loading,
.maplibregl-ctrl-scale-custom,
.maplibregl-ctrl-mouse-coordinates {
  background-color: hsla(0, 0%, 100%, 0.7529411764705882);
  border-radius: 4px;
  color: #333;
  padding: 4px;
  min-height: 32px;
}
.maplibregl-ctrl-mouse-coordinates-loading {
  max-width: 30px;
  font-size: 10px;
}
.maplibregl-ctrl-scale-custom {
  min-width: 100px;
  font-size: 14px;
}
.maplibregl-ctrl-mouse-coordinates {
  min-width: 160px;
  font-size: 14px;
}
.maplibregl-ctrl-mouse-coordinates > div:not(:last-child) {
  border-right: 1px solid #7c7c7c;
  padding-right: 4px;
}
.maplibregl-ctrl-mouse-coordinates > div:not(:first-child) {
  padding-left: 4px;
}
.icon-clickable {
  cursor: pointer;
}

.icon-clickable:hover,
.icon-clickable:focus {
  filter: brightness(1.1);
}

.icon-clickable:active {
  filter: brightness(0.8);
}

.crs-select:focus {
  outline: none;
}
</style>
